import React from 'react';
import { Box } from '@mui/material';
import { AvailabilityWindow } from './types';
import { themeColors } from './themeColors';

interface ContinuousTimelineHeaderProps {
  startDate: Date;
  totalHours: number;
  hourWidth: number;
  currentTime: Date;
  availabilityWindow?: AvailabilityWindow | null;
}

export function ContinuousTimelineHeader({
  startDate,
  totalHours,
  hourWidth,
  currentTime,
  availabilityWindow
}: ContinuousTimelineHeaderProps) {
  const renderTimeMarkers = () => {
    const markers = [];
    let lastDateLabel = '';
    
    for (let i = 0; i < totalHours; i++) {
      const currentHourDate = new Date(startDate);
      currentHourDate.setHours(startDate.getHours() + i);
      
      const hour = currentHourDate.getHours();
      const dateLabel = currentHourDate.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short'
      });
      
      const showDateLabel = dateLabel !== lastDateLabel;
      if (showDateLabel) {
        lastDateLabel = dateLabel;
      }
      
      // Show date label at midnight or when date changes
      const isDateBoundary = hour === 0 || showDateLabel;
      
      // Format hour: remove :00 and leading zero for single digits
      const hourLabel = hour === 0 ? '0' : hour.toString();
      
      markers.push(
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: i * hourWidth
          }}
        >
          {/* Date label - only at date boundaries */}
          {isDateBoundary && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              borderBottom: `1px solid ${themeColors.border}`,
              px: 0.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: themeColors.foreground,
              textAlign: 'center',
              zIndex: 10
            }}>
              {dateLabel}
            </Box>
          )}
          
          {/* Hour label - centered on the grid line */}
          <Box 
            sx={{
              position: 'absolute',
              fontSize: '0.75rem',
              color: themeColors.foreground,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              zIndex: 10,
              top: isDateBoundary ? '28px' : '4px',
              left: '2px'
            }}
          >
            {hourLabel}
          </Box>
        </Box>
      );
    }
    
    return markers;
  };
  
  return (
    <Box sx={{ position: 'relative', height: 64 }}>
      <Box sx={{ position: 'relative', height: '100%', width: totalHours * hourWidth }}>
        {/* Day alternating background stripes in header */}
        {(() => {
          const backgrounds = [];
          const totalDays = Math.ceil(totalHours / 24);
          for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
            const hoursSinceStart = dayIndex * 24;
            const isEvenDay = dayIndex % 2 === 0;
            backgrounds.push(
              <Box
                key={`header-bg-${dayIndex}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: hoursSinceStart * hourWidth,
                  width: 24 * hourWidth,
                  bgcolor: isEvenDay ? themeColors.dayStripeEven : themeColors.dayStripeOdd
                }}
              />
            );
          }
          return backgrounds;
        })()}
        
        {renderTimeMarkers()}
        
        {/* Day boundary lines in header - rendered on top */}
        {(() => {
          const lines = [];
          for (let i = 0; i < totalHours; i++) {
            const currentHourDate = new Date(startDate);
            currentHourDate.setHours(startDate.getHours() + i);
            const isDayBoundary = currentHourDate.getHours() === 0;
            
            if (isDayBoundary) {
              lines.push(
                <Box
                  key={`line-${i}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: i * hourWidth,
                    borderLeft: `3px solid ${themeColors.borderStrong}`,
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                />
              );
            }
          }
          return lines;
        })()}
        
        {/* All vertical grid lines in header - rendered on top */}
        {(() => {
          const gridLines = [];
          for (let i = 0; i <= totalHours; i++) {
            const currentHourDate = new Date(startDate);
            currentHourDate.setHours(startDate.getHours() + i);
            const isDayBoundary = currentHourDate.getHours() === 0;
            
            // Skip rendering regular line if it's a day boundary (already rendered above)
            if (!isDayBoundary) {
              gridLines.push(
                <Box
                  key={`grid-line-${i}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: i * hourWidth,
                    borderLeft: `1px solid ${themeColors.border}`,
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                />
              );
            }
          }
          return gridLines;
        })()}
        
        {/* Trip intent vertical bars in header */}
        {availabilityWindow && (() => {
          const startHours = (availabilityWindow.startTime.getTime() - startDate.getTime()) / (1000 * 60 * 60);
          const endHours = (availabilityWindow.endTime.getTime() - startDate.getTime()) / (1000 * 60 * 60);
          const leftStart = startHours * hourWidth;
          const leftEnd = endHours * hourWidth;
          const width = leftEnd - leftStart;
          
          const formatTime = (date: Date) => {
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm} ${month} ${day}`;
          };
          
          const duration = Math.round((availabilityWindow.endTime.getTime() - availabilityWindow.startTime.getTime()) / (1000 * 60));
          
          const formatDuration = (minutes: number) => {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            if (hours === 0) {
              return `${mins}m`;
            } else if (mins === 0) {
              return `${hours}h`;
            } else {
              return `${hours}h ${mins}m`;
            }
          };
          
          return (
            <>
              {/* Pickup time bar (green) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: leftStart,
                  width: 4,
                  bgcolor: themeColors.success,
                  opacity: 0.4,
                  pointerEvents: 'none',
                  zIndex: 30
                }}
              />
              
              {/* Pickup time label */}
              <Box sx={{ position: 'absolute', left: leftStart, top: 32, transform: 'translateX(-50%)', pointerEvents: 'auto', zIndex: 40 }}>
                <Box sx={{ display: 'inline-block', whiteSpace: 'nowrap', bgcolor: themeColors.success, color: '#ffffff', px: 1.5, py: 0.75, borderRadius: 1.5, boxShadow: 3, fontSize: '0.75rem', fontWeight: 700 }}>
                  {formatTime(availabilityWindow.startTime)}
                </Box>
                <Box sx={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `5px solid ${themeColors.success}` }} />
              </Box>
              
              {/* Drop time bar (red) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: leftEnd,
                  width: 4,
                  bgcolor: themeColors.destructive,
                  opacity: 0.4,
                  pointerEvents: 'none',
                  zIndex: 30
                }}
              />
              
              {/* Drop time label */}
              <Box sx={{ position: 'absolute', left: leftEnd, top: 32, transform: 'translateX(-50%)', pointerEvents: 'auto', zIndex: 40 }}>
                <Box sx={{ display: 'inline-block', whiteSpace: 'nowrap', bgcolor: themeColors.destructive, color: '#ffffff', px: 1.5, py: 0.75, borderRadius: 1.5, boxShadow: 3, fontSize: '0.75rem', fontWeight: 700 }}>
                  {formatTime(availabilityWindow.endTime)}
                </Box>
                <Box sx={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `5px solid ${themeColors.destructive}` }} />
              </Box>
              
              {/* Duration label in the middle */}
              {width > 150 && (
                <Box sx={{ position: 'absolute', left: leftStart + width / 2, top: 32, transform: 'translateX(-50%)', pointerEvents: 'auto', zIndex: 40 }}>
                  <Box sx={{ display: 'inline-block', whiteSpace: 'nowrap', bgcolor: '#2563eb', color: '#ffffff', px: 1.5, py: 0.75, borderRadius: 1.5, boxShadow: 3, fontSize: '0.75rem', fontWeight: 600 }}>
                    Duration: {formatDuration(duration)}
                  </Box>
                  <Box sx={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #2563eb' }} />
                </Box>
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );
}